import React, { useState, useEffect } from 'react';
import { useCRM } from '../../contexts/CRMContext';
import { PERMISSIONS, hasPermission } from '../../utils/permissions';
import { Link } from 'react-router-dom';
import { analyticsAPI, formsAPI, campaignsAPI, workflowAPI } from '../../utils/crmApi';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '../../contexts/BrandingContext';
import {
    ChartBarIcon,
    PlusIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ArrowDownTrayIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    UsersIcon,
    CurrencyDollarIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    DocumentTextIcon,
    MapPinIcon,
    CalendarIcon,
    HeartIcon,
    StarIcon,
    ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';
import toast from 'react-hot-toast';
import { useDialog, ConfirmationDialog } from '../../components/kibo-ui/dialog';
import {
    TableProvider,
    Table,
    TableHeader,
    TableSearch,
    TableHead,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableFooter,
    StatusBadge,
    TableAvatar
} from '../../components/kibo-ui/table';

const Analytics = () => {
    const { openDialog } = useDialog();
    const { user, currentAgency } = useCRM();
    const { branding } = useBranding();
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const primaryColor = branding?.primaryColor || '#3b82f6';
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20 });
    const [selectedReports, setSelectedReports] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [forms, setForms] = useState([]);
    const [formsLoading, setFormsLoading] = useState(false);
    const [formsAnalytics, setFormsAnalytics] = useState({});
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [campaignsAnalytics, setCampaignsAnalytics] = useState({});
    const [workflows, setWorkflows] = useState([]);
    const [workflowsLoading, setWorkflowsLoading] = useState(false);

    // Report type colors
    const getTypeColor = (type) => {
        const colors = {
            'financial': 'bg-green-100 text-green-700',
            'client': 'bg-blue-100 text-blue-700',
            'performance': 'bg-purple-100 text-purple-700',
            'marketing': 'bg-pink-100 text-pink-700',
            'operational': 'bg-muted text-foreground',
            'custom': 'bg-indigo-100 text-indigo-700'
        };
        return colors[type] || 'bg-muted text-foreground';
    };

    // Report type icons
    const getTypeIcon = (type) => {
        const icons = {
            'financial': '💰',
            'client': '👥',
            'performance': '📊',
            'marketing': '📢',
            'operational': '⚙️',
            'custom': '📈'
        };
        return icons[type] || '📊';
    };

    useEffect(() => {
        loadReports();
        loadDashboardData();
        if (activeTab === 'forms') {
            loadFormsAnalytics();
        } else if (activeTab === 'campaigns') {
            loadCampaignsAnalytics();
        } else if (activeTab === 'workflows') {
            loadWorkflowsAnalytics();
        }
    }, [search, typeFilter, dateFilter, pagination.page, activeTab]);
    
    const loadFormsAnalytics = async () => {
        try {
            setFormsLoading(true);
            const { data: formsData } = await formsAPI.getAll({ status: 'published' });
            const formsList = formsData.data || [];
            setForms(formsList);
            
            // Load analytics for each published form
            const analyticsPromises = formsList.map(async (form) => {
                try {
                    const { data: analyticsData } = await formsAPI.getAnalytics(form._id, { days: 30 });
                    return { formId: form._id, analytics: analyticsData.data };
                } catch (e) {
                    return { formId: form._id, analytics: null };
                }
            });
            
            const results = await Promise.all(analyticsPromises);
            const analyticsMap = {};
            results.forEach(({ formId, analytics }) => {
                analyticsMap[formId] = analytics;
            });
            setFormsAnalytics(analyticsMap);
        } catch (e) {
            console.error('Failed to load forms analytics:', e);
        } finally {
            setFormsLoading(false);
        }
    };

    const loadCampaignsAnalytics = async () => {
        try {
            setCampaignsLoading(true);
            const { data: campaignsData } = await campaignsAPI.getAll({ limit: 1000 });
            const campaignsList = campaignsData.data || [];
            setCampaigns(campaignsList);
            
            // Load analytics for each campaign
            const analyticsPromises = campaignsList.map(async (campaign) => {
                try {
                    const { data: analyticsData } = await campaignsAPI.getAnalytics(campaign._id, { granularity: 'day' });
                    return { campaignId: campaign._id, analytics: analyticsData.data };
                } catch (e) {
                    return { campaignId: campaign._id, analytics: null };
                }
            });
            
            const results = await Promise.all(analyticsPromises);
            const analyticsMap = {};
            results.forEach(({ campaignId, analytics }) => {
                analyticsMap[campaignId] = analytics;
            });
            setCampaignsAnalytics(analyticsMap);
        } catch (e) {
            console.error('Failed to load campaigns analytics:', e);
        } finally {
            setCampaignsLoading(false);
        }
    };

    const loadWorkflowsAnalytics = async () => {
        try {
            setWorkflowsLoading(true);
            const { data: workflowsData } = await workflowAPI.getAll({ limit: 1000 });
            const workflowsList = workflowsData.data || [];
            setWorkflows(workflowsList);
        } catch (e) {
            console.error('Failed to load workflows analytics:', e);
        } finally {
            setWorkflowsLoading(false);
        }
    };

    const loadReports = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(search && { search }),
                ...(typeFilter && { type: typeFilter }),
                ...(dateFilter && { dateRange: dateFilter })
            };

            const response = await analyticsAPI.getAll(params);
            if (response.data.success) {
                setReports(response.data.data);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Failed to load reports:', error);
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const loadDashboardData = async () => {
        try {
            const response = await analyticsAPI.getDashboard();
            
            // Handle both direct response.data and nested response.data.data structures
            const responseData = response?.data;
            if (!responseData) {
                console.error('No response data received');
                return;
            }
            
            // Check if response has success flag
            if (responseData.success !== undefined && !responseData.success) {
                console.warn('Analytics API response not successful:', responseData);
                setDashboardData({
                    totalClients: 0,
                    totalRevenue: 0,
                    revenue: 0,
                    totalTasks: 0,
                    conversionRate: 0,
                    taskCompletionRate: 0,
                    clientGrowth: 0,
                    revenueGrowth: 0,
                    conversionGrowth: 0,
                    taskGrowth: 0
                });
                return;
            }
            
            // Extract data - handle both response.data.data and response.data structures
            const data = responseData.data || responseData;
            const summary = data.summary || {};
            const clients = summary.clients || {};
            const tasks = summary.tasks || {};
            const accounts = summary.accounts || {};
            
            // Calculate rates
            const totalTasks = tasks.total || 0;
            const completedTasks = tasks.completed || 0;
            const taskCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
            
            // Calculate conversion rate (active clients / total clients)
            const totalClients = clients.total || 0;
            const activeClients = clients.active || 0;
            const conversionRate = totalClients > 0 ? Math.round((activeClients / totalClients) * 100) : 0;
            
            const dashboardDataObj = {
                totalClients: totalClients,
                totalRevenue: data.totalRevenue || 0,
                revenue: data.revenue || 0,
                totalTasks: totalTasks,
                conversionRate: conversionRate,
                taskCompletionRate: taskCompletionRate,
                clientGrowth: data.clientGrowth || 0,
                revenueGrowth: data.revenueGrowth || 0,
                conversionGrowth: data.conversionGrowth || 0,
                taskGrowth: data.taskGrowth || 0,
                // Include nested data for other parts of the component
                summary: summary,
                breakdown: data.breakdown || {},
                recent: data.recent || {}
            };
            
            setDashboardData(dashboardDataObj);
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
            console.error('Error details:', error.response?.data || error.message);
            // Set default data structure on error
            setDashboardData({
                totalClients: 0,
                totalRevenue: 0,
                revenue: 0,
                totalTasks: 0,
                conversionRate: 0,
                taskCompletionRate: 0,
                clientGrowth: 0,
                revenueGrowth: 0,
                conversionGrowth: 0,
                taskGrowth: 0
            });
        }
    };

    // Generate time-series data for charts
    const generateClientAcquisitionData = () => {
        // Generate last 7 days structure
        const last7Days = [];
        const today = new Date();
        for (let i = 6; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i);
            const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
            
            last7Days.push({
                date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                clients: 0,
                fullDate: dateKey
            });
        }

        // If we have recent clients, populate the data
        if (dashboardData?.recent?.clients && dashboardData.recent.clients.length > 0) {
            dashboardData.recent.clients.forEach(client => {
                if (client.createdAt) {
                    const clientDate = new Date(client.createdAt);
                    const clientDateKey = clientDate.toISOString().split('T')[0];
                    
                    // Find matching day in last7Days and increment
                    const dayData = last7Days.find(d => d.fullDate === clientDateKey);
                    if (dayData) {
                        dayData.clients++;
                    }
                }
            });
        }

        return last7Days;
    };

    // Generate revenue trend data (placeholder since revenue data isn't available yet)
    const generateRevenueTrendData = () => {
        // Generate last 7 days with zero revenue (since revenue data isn't tracked yet)
        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
            const date = new Date();
            date.setDate(date.getDate() - i);
            last7Days.push({
                date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                revenue: 0,
                fullDate: date.toISOString().split('T')[0]
            });
        }
        return last7Days;
    };

    const clientAcquisitionData = generateClientAcquisitionData();
    const revenueTrendData = generateRevenueTrendData();

    // Custom tooltip for charts
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-card border border-border rounded-lg shadow-lg p-3">
                    <p className="text-sm font-medium text-foreground mb-2">{label}</p>
                    {payload.map((entry, index) => {
                        const value = entry.dataKey === 'revenue' 
                            ? `€${entry.value.toLocaleString()}` 
                            : entry.value;
                        const label = entry.dataKey === 'revenue' ? 'Revenue' : 'New Clients';
                        return (
                            <p key={index} className="text-sm font-medium" style={{ color: entry.color }}>
                                {label}: {value}
                            </p>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    const handleSelectReport = (reportId) => {
        setSelectedReports(prev => 
            prev.includes(reportId) 
                ? prev.filter(id => id !== reportId)
                : [...prev, reportId]
        );
    };

    const handleSelectAll = () => {
        if (selectedReports.length === reports.length) {
            setSelectedReports([]);
        } else {
            setSelectedReports(reports.map(report => report._id));
        }
    };

    const handleBulkAction = (action) => {
        if (selectedReports.length === 0) return;
        
        const actionMessages = {
            'export': 'export',
            'schedule': 'schedule',
            'delete': 'delete'
        };

        openDialog({
            title: `${actionMessages[action]} Reports`,
            description: `Are you sure you want to ${actionMessages[action]} ${selectedReports.length} report(s)?`,
            confirmText: actionMessages[action].charAt(0).toUpperCase() + actionMessages[action].slice(1),
            cancelText: 'Cancel',
            variant: action === 'delete' ? 'danger' : 'warning',
            onConfirm: async () => {
                try {
                    setBulkActionLoading(true);
                    await analyticsAPI.bulkAction(selectedReports, action);
                    toast.success(`${selectedReports.length} report(s) ${actionMessages[action]}ed successfully`);
                    setSelectedReports([]);
                    loadReports();
                } catch (error) {
                    console.error('Error performing bulk action:', error);
                    toast.error('Failed to perform bulk action');
                } finally {
                    setBulkActionLoading(false);
                }
            }
        });
    };

    const handleDeleteReport = (reportId) => {
        openDialog({
            title: 'Delete Report',
            description: 'Are you sure you want to delete this report? This action cannot be undone.',
            confirmText: 'Delete Report',
            cancelText: 'Cancel',
            variant: 'danger',
            onConfirm: async () => {
                try {
                    await analyticsAPI.delete(reportId);
                    toast.success('Report deleted successfully');
                    loadReports();
                } catch (error) {
                    console.error('Error deleting report:', error);
                    toast.error('Failed to delete report');
                }
            }
        });
    };

    const handleGenerateReport = async (reportId) => {
        try {
            await analyticsAPI.generate(reportId);
            toast.success('Report generated successfully!');
            loadReports();
        } catch (error) {
            console.error('Error generating report:', error);
            toast.error('Failed to generate report');
        }
    };

    const handleDownloadReport = async (reportId) => {
        try {
            const response = await analyticsAPI.download(reportId);
            // Create blob and download
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `report-${reportId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading report:', error);
            toast.error('Failed to download report');
        }
    };

    const columns = [
        { accessor: 'select', label: '', sortable: false, width: 'w-12' },
        { accessor: 'name', label: 'Report', sortable: true },
        { accessor: 'type', label: 'Type', sortable: true },
        { accessor: 'status', label: 'Status', sortable: true },
        { accessor: 'lastGenerated', label: 'Last Generated', sortable: true },
        { accessor: 'schedule', label: 'Schedule', sortable: true },
        { accessor: 'actions', label: 'Actions', sortable: false }
    ];

    const typeOptions = [
        { value: '', label: 'All Types' },
        { value: 'financial', label: 'Financial' },
        { value: 'client', label: 'Client' },
        { value: 'performance', label: 'Performance' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'operational', label: 'Operational' },
        { value: 'custom', label: 'Custom' }
    ];

    const dateFilterOptions = [
        { value: '', label: 'All Time' },
        { value: 'today', label: 'Today' },
        { value: 'this_week', label: 'This Week' },
        { value: 'this_month', label: 'This Month' },
        { value: 'this_quarter', label: 'This Quarter' },
        { value: 'this_year', label: 'This Year' },
        { value: 'last_30_days', label: 'Last 30 Days' },
        { value: 'last_90_days', label: 'Last 90 Days' }
    ];

    const formatCurrency = (amount, currency = 'EUR') => {
        return new Intl.NumberFormat('en-EU', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatPercentage = (value) => {
        if (value === undefined || value === null || isNaN(value)) {
            return '0.0%';
        }
        return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
    };

    const getTrendIcon = (value) => {
        if (value === undefined || value === null || isNaN(value)) {
            return <div className="h-4 w-4" />;
        }
        if (value > 0) return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
        if (value < 0) return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
        return <div className="h-4 w-4" />;
    };

    const getTrendColor = (value) => {
        if (value === undefined || value === null || isNaN(value)) {
            return 'text-muted-foreground';
        }
        if (value > 0) return 'text-green-600';
        if (value < 0) return 'text-red-600';
        return 'text-muted-foreground';
    };

    const tabs = [
        { id: 'overview', name: 'Overview', icon: ChartBarIcon },
        { id: 'forms', name: 'Forms', icon: ClipboardDocumentListIcon },
        { id: 'campaigns', name: 'Campaigns', icon: HeartIcon },
        { id: 'workflows', name: 'Workflows', icon: ClockIcon },
        { id: 'financial', name: 'Financial', icon: CurrencyDollarIcon },
        { id: 'clients', name: 'Clients', icon: UsersIcon },
        { id: 'performance', name: 'Performance', icon: CheckCircleIcon },
        { id: 'reports', name: 'Reports', icon: DocumentTextIcon }
    ];

    return (
        <div className="space-y-6" data-tour="analytics">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Analytics & Reporting</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Track performance, generate insights, and make data-driven decisions
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 flex items-center gap-3">
                    {selectedReports.length > 0 && hasPermission(user, PERMISSIONS.EXPORT_DATA, currentAgency?._id) && (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleBulkAction('export')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 hover:opacity-80 transition-opacity"
                                style={{
                                  color: primaryColor,
                                  backgroundColor: `${primaryColor}15`,
                                  borderColor: `${primaryColor}40`
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = `${primaryColor}25`;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = `${primaryColor}15`;
                                }}
                            >
                                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                                Export
                            </button>
                            <button
                                onClick={() => handleBulkAction('schedule')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50"
                            >
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                Schedule
                            </button>
                            <button
                                onClick={() => handleBulkAction('delete')}
                                disabled={bulkActionLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                            >
                                <TrashIcon className="h-4 w-4 mr-1" />
                                Delete
                            </button>
                        </div>
                    )}
                    <Link
                        to="/analytics/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2"
                        style={{ 
                          backgroundColor: primaryColor,
                          '--tw-ring-color': primaryColor
                        }}
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Create Report
                    </Link>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-border overflow-x-auto overflow-y-hidden scrollbar-thin">
                <nav className="-mb-px flex space-x-8 min-w-max">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`${
                                activeTab === tab.id
                                    ? 'border-b-2'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                            } whitespace-nowrap py-2 px-1 font-medium text-sm flex items-center flex-shrink-0`}
                            style={activeTab === tab.id ? {
                                borderBottomColor: primaryColor,
                                color: primaryColor
                            } : {}}
                        >
                            <tab.icon className="h-4 w-4 mr-2" />
                            {tab.name}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Dashboard Overview */}
            {activeTab === 'overview' && dashboardData && (
                <div className="space-y-6">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <UsersIcon className="h-8 w-8" style={{ color: primaryColor }} />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground">Total Clients</p>
                                    <p className="text-2xl font-semibold text-foreground">{dashboardData.totalClients}</p>
                                    <div className="flex items-center text-xs">
                                        {getTrendIcon(dashboardData.clientGrowth)}
                                        <span className={`ml-1 ${getTrendColor(dashboardData.clientGrowth)}`}>
                                            {formatPercentage(dashboardData.clientGrowth)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground">Revenue</p>
                                    <p className="text-2xl font-semibold text-foreground">
                                        {formatCurrency(dashboardData.revenue)}
                                    </p>
                                    <div className="flex items-center text-xs">
                                        {getTrendIcon(dashboardData.revenueGrowth)}
                                        <span className={`ml-1 ${getTrendColor(dashboardData.revenueGrowth)}`}>
                                            {formatPercentage(dashboardData.revenueGrowth)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <StarIcon className="h-8 w-8 text-yellow-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground">Client Conversion</p>
                                    <p className="text-2xl font-semibold text-foreground">{dashboardData.conversionRate}%</p>
                                    <div className="flex items-center text-xs">
                                        {getTrendIcon(dashboardData.conversionGrowth)}
                                        <span className={`ml-1 ${getTrendColor(dashboardData.conversionGrowth)}`}>
                                            {formatPercentage(dashboardData.conversionGrowth)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <CheckCircleIcon className="h-8 w-8 text-purple-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground">Task Completion</p>
                                    <p className="text-2xl font-semibold text-foreground">{dashboardData.taskCompletionRate}%</p>
                                    <div className="flex items-center text-xs">
                                        {getTrendIcon(dashboardData.taskGrowth)}
                                        <span className={`ml-1 ${getTrendColor(dashboardData.taskGrowth)}`}>
                                            {formatPercentage(dashboardData.taskGrowth)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Revenue Trend Chart */}
                        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-foreground">Revenue Trend</h3>
                                <span className="text-xs text-muted-foreground">Last 7 days</span>
                            </div>
                            {revenueTrendData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={256}>
                                    <AreaChart data={revenueTrendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={primaryColor} stopOpacity={0.3} />
                                                <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} />
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis 
                                            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickFormatter={(value) => `€${value}`}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area 
                                            type="monotone" 
                                            dataKey="revenue" 
                                            stroke={primaryColor} 
                                            strokeWidth={2}
                                            fill="url(#revenueGradient)"
                                            dot={{ fill: primaryColor, strokeWidth: 2, r: 4 }}
                                            activeDot={{ r: 6, stroke: primaryColor, strokeWidth: 2 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-64 flex items-center justify-center text-muted-foreground">
                                    <div className="text-center">
                                        <CurrencyDollarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No revenue data available</p>
                                        <p className="text-xs mt-1">Revenue tracking will be available soon</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Client Acquisition Chart */}
                        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-foreground">Client Acquisition</h3>
                                <span className="text-xs text-muted-foreground">Last 7 days</span>
                            </div>
                            {clientAcquisitionData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={256}>
                                    <LineChart data={clientAcquisitionData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} />
                                        <XAxis 
                                            dataKey="date" 
                                            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis 
                                            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                                            axisLine={false}
                                            tickLine={false}
                                            allowDecimals={false}
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Line 
                                            type="monotone" 
                                            dataKey="clients" 
                                            stroke={primaryColor} 
                                            strokeWidth={3}
                                            dot={{ fill: primaryColor, strokeWidth: 2, r: 5 }}
                                            activeDot={{ r: 7, stroke: primaryColor, strokeWidth: 2 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-64 flex items-center justify-center text-muted-foreground">
                                    <div className="text-center">
                                        <UsersIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No client data available</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Reports Section */}
            {activeTab === 'reports' && (
                <>
                    {/* Filters */}
                    <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                                <select
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                    className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': primaryColor }}
                                >
                                    {typeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">Date Range</label>
                                <select
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    className="w-full border border-input rounded-lg px-3 py-2 text-sm b bg-background text-foregroundg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    style={{ '--tw-ring-color': primaryColor }}
                                >
                                    {dateFilterOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={() => {
                                        setTypeFilter('');
                                        setDateFilter('');
                                        setSearch('');
                                    }}
                                    className="w-full px-4 py-2 text-sm font-medium text-foreground bg-muted border border-border rounded-lg hover:bg-muted/80"
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Enhanced Table with Kibo UI */}
                    <TableProvider 
                        data={reports} 
                        columns={columns}
                        onSort={(key, direction) => console.log('🔄 Table sort:', { key, direction })}
                        onFilter={(key, value) => console.log('🔍 Table filter:', { key, value })}
                    >
                        <Table>
                            <TableHeader>
                                <TableSearch placeholder="Search reports by name, type, or description..." />
                            </TableHeader>
                            
                            <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <TableHead>
                                        <tr>
                                            {columns.map((column) => (
                                                <TableHeaderCell key={column.accessor} column={column}>
                                                    {column.accessor === 'select' ? (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedReports.length === reports.length && reports.length > 0}
                                                            onChange={handleSelectAll}
                                                            className="h-4 w-4 border-input rounded"
                                                            style={{ accentColor: primaryColor }}
                                                        />
                                                    ) : (
                                                        column.label
                                                    )}
                                                </TableHeaderCell>
                                            ))}
                                        </tr>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={columns.length} className="px-6 py-12 text-center">
                                                    <div className="flex items-center justify-center">
                                                        <div 
                                                            className="animate-spin rounded-full h-8 w-8 border-b-2"
                                                            style={{ borderBottomColor: primaryColor }}
                                                        ></div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : reports.length === 0 ? (
                                            <tr>
                                                <td colSpan={columns.length} className="px-6 py-12 text-center">
                                                    <div className="text-center">
                                                        <ChartBarIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                                        <h3 className="mt-2 text-sm font-medium text-foreground">No reports found</h3>
                                                        <p className="mt-1 text-sm text-muted-foreground">
                                                            Get started by creating your first report.
                                                        </p>
                                                        <Link
                                                            to="/analytics/new"
                                                            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-opacity"
                                                            style={{ backgroundColor: primaryColor }}
                                                        >
                                                            <PlusIcon className="h-5 w-5 mr-2" />
                                                            Create Report
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            reports.map((report) => (
                                                <TableRow key={report._id}>
                                                    <TableCell className="w-12">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedReports.includes(report._id)}
                                                            onChange={() => handleSelectReport(report._id)}
                                                            className="h-4 w-4 border-input rounded"
                                                            style={{ accentColor: primaryColor }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center">
                                                            <div className="text-2xl mr-3">{getTypeIcon(report.type)}</div>
                                                            <div>
                                                                <Link
                                                                    to={`/analytics/${report._id}`}
                                                                    className="text-sm font-medium text-foreground hover:opacity-80 transition-opacity"
                                                                    style={{}}
                                                                    onMouseEnter={(e) => {
                                                                        e.currentTarget.style.color = primaryColor;
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        e.currentTarget.style.color = '#111827';
                                                                    }}
                                                                >
                                                                    {report.name}
                                                                </Link>
                                                                <div className="text-xs text-muted-foreground">
                                                                    {report.description}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <StatusBadge 
                                                            status={report.type}
                                                            className={getTypeColor(report.type)}
                                                        >
                                                            {report.type.charAt(0).toUpperCase() + report.type.slice(1)}
                                                        </StatusBadge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            report.status === 'ready' ? 'bg-green-100 text-green-700' :
                                                            report.status === 'generating' ? 'bg-yellow-100 text-yellow-700' :
                                                            report.status === 'error' ? 'bg-red-100 text-red-700' :
                                                            'bg-muted text-foreground'
                                                        }`}>
                                                            {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm text-foreground">
                                                            {report.lastGenerated ? 
                                                                new Date(report.lastGenerated).toLocaleDateString() : 
                                                                'Never'
                                                            }
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {report.lastGenerated ? 
                                                                new Date(report.lastGenerated).toLocaleTimeString() : 
                                                                'Not generated'
                                                            }
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="text-sm text-foreground">
                                                            {report.schedule || 'Manual'}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {report.nextRun ? 
                                                                `Next: ${new Date(report.nextRun).toLocaleDateString()}` : 
                                                                'No schedule'
                                                            }
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleGenerateReport(report._id)}
                                                                className="text-sm font-medium hover:opacity-80 transition-opacity"
                                                                style={{ color: primaryColor }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.color = primaryColor;
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.color = primaryColor;
                                                                }}
                                                                title="Generate Report"
                                                            >
                                                                Generate
                                                            </button>
                                                            <button
                                                                onClick={() => handleDownloadReport(report._id)}
                                                                className="text-green-600 hover:text-green-800 text-sm font-medium"
                                                                title="Download"
                                                            >
                                                                <ArrowDownTrayIcon className="h-4 w-4" />
                                                            </button>
                                                            <Link
                                                                to={`/analytics/${report._id}`}
                                                                className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                                title="View"
                                                            >
                                                                <EyeIcon className="h-4 w-4" />
                                                            </Link>
                                                            <Link
                                                                to={`/analytics/${report._id}/edit`}
                                                                className="text-muted-foreground hover:text-foreground text-sm font-medium"
                                                                title="Edit"
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                            </Link>
                                                            <button
                                                                onClick={() => handleDeleteReport(report._id)}
                                                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                                                                title="Delete"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </table>
                            </div>
                            
                            <TableFooter>
                                <div className="px-6 py-3 border-t border-border">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-muted-foreground">
                                            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                                disabled={pagination.page === 1}
                                                className="px-3 py-1 text-sm text-foreground bg-background border border-border rounded hover:bg-muted disabled:opacity-50"
                                            >
                                                Previous
                                            </button>
                                            <span className="px-3 py-1 text-sm text-muted-foreground">
                                                Page {pagination.page} of {pagination.pages}
                                            </span>
                                            <button
                                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                                disabled={pagination.page === pagination.pages}
                                                className="px-3 py-1 text-sm text-foreground bg-background border border-border rounded hover:bg-muted disabled:opacity-50"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </TableFooter>
                        </Table>
                    </TableProvider>
                </>
            )}

            {/* Forms Analytics Tab */}
            {activeTab === 'forms' && (
                <div className="space-y-6">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">Form Performance</h2>
                                <p className="text-sm text-muted-foreground mt-1">Track views, starts, and conversion rates across all your published forms</p>
                            </div>
                            <Link
                                to="/forms"
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg hover:opacity-80 transition-opacity"
                                style={{
                                    color: primaryColor,
                                    backgroundColor: `${primaryColor}15`,
                                    borderColor: `${primaryColor}40`
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = `${primaryColor}25`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = `${primaryColor}15`;
                                }}
                            >
                                <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                                Manage Forms
                            </Link>
                        </div>
                        
                        {formsLoading ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                <p className="mt-2 text-sm text-muted-foreground">Loading form analytics...</p>
                            </div>
                        ) : forms.length === 0 ? (
                            <div className="text-center py-12">
                                <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                <h3 className="mt-2 text-sm font-medium text-foreground">No published forms</h3>
                                <p className="mt-1 text-sm text-muted-foreground">Publish a form to start tracking analytics.</p>
                                <Link
                                    to="/forms"
                                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <PlusIcon className="h-5 w-5 mr-2" />
                                    Create Form
                                </Link>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-muted">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Form Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Views</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Starts</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Submissions</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">View → Start</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Start → Submit</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-card divide-y divide-gray-200">
                                        {forms.map((form) => {
                                            const analytics = formsAnalytics[form._id] || {};
                                            const viewToStart = analytics.viewToStartRate || 0;
                                            const conversionRate = analytics.conversionRate || 0;
                                            
                                            return (
                                                <tr key={form._id} className="hover:bg-muted">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-foreground">{form.name}</div>
                                                        <div className="text-xs text-muted-foreground">{form.status}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{analytics.views || 0}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{analytics.starts || 0}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{analytics.submissions || 0}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="flex-1 bg-muted rounded-full h-2 mr-2 max-w-[60px]">
                                                                <div 
                                                                    className="h-2 rounded-full"
                                                                    style={{ 
                                                                        backgroundColor: primaryColor,
                                                                        width: `${Math.min(viewToStart, 100)}%`
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="text-sm text-foreground">{viewToStart}%</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="flex-1 bg-muted rounded-full h-2 mr-2 max-w-[60px]">
                                                                <div 
                                                                    className="bg-green-500 h-2 rounded-full" 
                                                                    style={{ width: `${Math.min(conversionRate, 100)}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-sm text-foreground">{conversionRate}%</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => navigate(`/forms/${form._id}/analytics`)}
                                                            className="hover:opacity-80 transition-opacity"
                                                            style={{ color: primaryColor }}
                                                        >
                                                            View Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    
                    {/* Forms Summary Cards */}
                    {forms.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {(() => {
                                const totalViews = Object.values(formsAnalytics).reduce((sum, a) => sum + (a?.views || 0), 0);
                                const totalStarts = Object.values(formsAnalytics).reduce((sum, a) => sum + (a?.starts || 0), 0);
                                const totalSubmissions = Object.values(formsAnalytics).reduce((sum, a) => sum + (a?.submissions || 0), 0);
                                const avgViewToStart = forms.length > 0 ? Math.round(Object.values(formsAnalytics).reduce((sum, a) => sum + (a?.viewToStartRate || 0), 0) / forms.length) : 0;
                                
                                return (
                                    <>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <EyeIcon className="h-8 w-8" style={{ color: primaryColor }} />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Total Views</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalViews}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <ClipboardDocumentListIcon className="h-8 w-8 text-purple-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Total Starts</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalStarts}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Total Submissions</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalSubmissions}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <ChartBarIcon className="h-8 w-8 text-yellow-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Avg View→Start</p>
                                                    <p className="text-2xl font-semibold text-foreground">{avgViewToStart}%</p>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}

            {/* Campaigns Analytics Tab */}
            {activeTab === 'campaigns' && (
                <div className="space-y-6">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">Campaign Performance</h2>
                                <p className="text-sm text-muted-foreground mt-1">Track sent, delivered, opened, and clicked rates across all your campaigns</p>
                            </div>
                            <Link
                                to="/campaigns"
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg hover:opacity-80 transition-opacity"
                                style={{
                                    color: primaryColor,
                                    backgroundColor: `${primaryColor}15`,
                                    borderColor: `${primaryColor}40`
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = `${primaryColor}25`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = `${primaryColor}15`;
                                }}
                            >
                                <HeartIcon className="h-4 w-4 mr-2" />
                                Manage Campaigns
                            </Link>
                        </div>
                        
                        {campaignsLoading ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                <p className="mt-2 text-sm text-muted-foreground">Loading campaign analytics...</p>
                            </div>
                        ) : campaigns.length === 0 ? (
                            <div className="text-center py-12">
                                <HeartIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                <h3 className="mt-2 text-sm font-medium text-foreground">No campaigns</h3>
                                <p className="mt-1 text-sm text-muted-foreground">Create a campaign to start tracking analytics.</p>
                                <Link
                                    to="/campaigns"
                                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <PlusIcon className="h-5 w-5 mr-2" />
                                    Create Campaign
                                </Link>
                            </div>
                        ) : (
                            <div className="overflow-x-auto scrollbar-thin">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-muted">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Campaign Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Sent</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Delivered</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Opened</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Clicked</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Open Rate</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Click Rate</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-card divide-y divide-gray-200">
                                        {campaigns.map((campaign) => {
                                            const analytics = campaignsAnalytics[campaign._id]?.overview || {};
                                            const sent = analytics.sent || 0;
                                            const delivered = analytics.delivered || 0;
                                            const opened = analytics.opened || 0;
                                            const clicked = analytics.clicked || 0;
                                            const openRate = analytics.openRate || 0;
                                            const clickRate = analytics.clickRate || 0;
                                            
                                            return (
                                                <tr key={campaign._id} className="hover:bg-muted">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-foreground">{campaign.name}</div>
                                                        <div className="text-xs text-muted-foreground">{campaign.type}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <StatusBadge type="campaign" status={campaign.status} />
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{sent}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{delivered}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{opened}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{clicked}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="flex-1 bg-muted rounded-full h-2 mr-2 max-w-[60px]">
                                                                <div 
                                                                    className="h-2 rounded-full"
                                                                    style={{ 
                                                                        backgroundColor: primaryColor,
                                                                        width: `${Math.min(openRate, 100)}%`
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="text-sm text-foreground">{openRate.toFixed(1)}%</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <div className="flex-1 bg-muted rounded-full h-2 mr-2 max-w-[60px]">
                                                                <div 
                                                                    className="bg-green-500 h-2 rounded-full" 
                                                                    style={{ width: `${Math.min(clickRate, 100)}%` }}
                                                                />
                                                            </div>
                                                            <span className="text-sm text-foreground">{clickRate.toFixed(1)}%</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => navigate(`/campaigns/${campaign._id}/analytics`)}
                                                            className="hover:opacity-80 transition-opacity"
                                                            style={{ color: primaryColor }}
                                                        >
                                                            View Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    
                    {/* Campaigns Summary Cards */}
                    {campaigns.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {(() => {
                                const totalSent = Object.values(campaignsAnalytics).reduce((sum, a) => sum + (a?.overview?.sent || 0), 0);
                                const totalDelivered = Object.values(campaignsAnalytics).reduce((sum, a) => sum + (a?.overview?.delivered || 0), 0);
                                const totalOpened = Object.values(campaignsAnalytics).reduce((sum, a) => sum + (a?.overview?.opened || 0), 0);
                                const totalClicked = Object.values(campaignsAnalytics).reduce((sum, a) => sum + (a?.overview?.clicked || 0), 0);
                                
                                return (
                                    <>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <HeartIcon className="h-8 w-8" style={{ color: primaryColor }} />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Total Sent</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalSent}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Total Delivered</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalDelivered}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <EyeIcon className="h-8 w-8 text-purple-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Total Opened</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalOpened}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <ChartBarIcon className="h-8 w-8 text-yellow-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Total Clicked</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalClicked}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}

            {/* Workflows Analytics Tab */}
            {activeTab === 'workflows' && (
                <div className="space-y-6">
                    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h2 className="text-lg font-semibold text-foreground">Workflow Performance</h2>
                                <p className="text-sm text-muted-foreground mt-1">Track executions, success rates, and performance across all your workflows</p>
                            </div>
                            <Link
                                to="/workflows"
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg hover:opacity-80 transition-opacity"
                                style={{
                                    color: primaryColor,
                                    backgroundColor: `${primaryColor}15`,
                                    borderColor: `${primaryColor}40`
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = `${primaryColor}25`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = `${primaryColor}15`;
                                }}
                            >
                                <ClockIcon className="h-4 w-4 mr-2" />
                                Manage Workflows
                            </Link>
                        </div>
                        
                        {workflowsLoading ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                                <p className="mt-2 text-sm text-muted-foreground">Loading workflow analytics...</p>
                            </div>
                        ) : workflows.length === 0 ? (
                            <div className="text-center py-12">
                                <ClockIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                                <h3 className="mt-2 text-sm font-medium text-foreground">No workflows</h3>
                                <p className="mt-1 text-sm text-muted-foreground">Create a workflow to start tracking analytics.</p>
                                <Link
                                    to="/workflows"
                                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <PlusIcon className="h-5 w-5 mr-2" />
                                    Create Workflow
                                </Link>
                            </div>
                        ) : (
                            <div className="overflow-x-auto scrollbar-thin">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-muted">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Workflow Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Category</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Total Executions</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Today</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Successful</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Failed</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Run</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-card divide-y divide-gray-200">
                                        {workflows.map((workflow) => {
                                            const totalExecutions = workflow.totalExecutions || 0;
                                            const todayExecutions = workflow.todayExecutions || 0;
                                            const successfulExecutions = workflow.successfulExecutions || 0;
                                            const failedExecutions = workflow.failedExecutions || 0;
                                            
                                            return (
                                                <tr key={workflow._id} className="hover:bg-muted">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-foreground">{workflow.name}</div>
                                                        <div className="text-xs text-muted-foreground">{workflow.description || 'No description'}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <StatusBadge type="workflow" status={workflow.status} />
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{workflow.category || 'N/A'}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{totalExecutions}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">{todayExecutions}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-green-600">{successfulExecutions}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-red-600">{failedExecutions}</div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-foreground">
                                                            {workflow.lastRunAt ? new Date(workflow.lastRunAt).toLocaleDateString() : 'Never'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <button
                                                            onClick={() => navigate(`/workflows/${workflow._id}`)}
                                                            className="hover:opacity-80 transition-opacity"
                                                            style={{ color: primaryColor }}
                                                        >
                                                            View Details
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    
                    {/* Workflows Summary Cards */}
                    {workflows.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {(() => {
                                const totalExecutions = workflows.reduce((sum, w) => sum + (w.totalExecutions || 0), 0);
                                const totalToday = workflows.reduce((sum, w) => sum + (w.todayExecutions || 0), 0);
                                const totalSuccessful = workflows.reduce((sum, w) => sum + (w.successfulExecutions || 0), 0);
                                const totalFailed = workflows.reduce((sum, w) => sum + (w.failedExecutions || 0), 0);
                                
                                return (
                                    <>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <ClockIcon className="h-8 w-8" style={{ color: primaryColor }} />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Total Executions</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalExecutions}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <CalendarIcon className="h-8 w-8 text-purple-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Today</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalToday}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Successful</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalSuccessful}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-card rounded-lg shadow-sm border border-border p-4">
                                            <div className="flex items-center">
                                                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                                                <div className="ml-4">
                                                    <p className="text-sm font-medium text-muted-foreground">Failed</p>
                                                    <p className="text-2xl font-semibold text-foreground">{totalFailed}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}

            {/* Other tabs content would go here */}
            {activeTab !== 'overview' && activeTab !== 'reports' && activeTab !== 'forms' && activeTab !== 'campaigns' && activeTab !== 'workflows' && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                    <div className="text-center">
                        <ChartBarIcon className="mx-auto h-12 w-12 text-muted-foreground" />
                        <h3 className="mt-2 text-sm font-medium text-foreground">
                            {tabs.find(tab => tab.id === activeTab)?.name} Analytics
                        </h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            This section will contain detailed analytics for {activeTab}.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Analytics;